/* eslint-disable */
import { Card, CardContent, Chip, Tooltip, withStyles } from '@material-ui/core';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import Fade from '@material-ui/core/Fade';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import LinearProgress from '@material-ui/core/LinearProgress';
import Typography from '@material-ui/core/Typography';
import withWidth, { isWidthDown } from '@material-ui/core/withWidth';
import FolderIcon from '@material-ui/icons/Folder';
import TuneIcon from '@material-ui/icons/Tune';
import { enableBodyScroll } from 'body-scroll-lock';
import cn from 'classnames';
import React from 'react';
import { connect } from 'react-redux';
import RefreshIcon from '@material-ui/icons/Refresh';
import { compose } from 'recompose';
import { bindActionCreators } from 'redux';
import AccountsContext from '../../Contexts/Accounts/Accounts.context';
import withServices from '../../Contexts/Services/WithServices.context';
import filtersActionCreators from '../../State/Store/Filters/Filters.actions';
import getFilters from '../../State/Store/Filters/Selectors/Filters.selector';
import ordersActionCreators from '../../State/Store/Orders/Orders.actions';
import ordersLoadingSelector from '../../State/Store/Orders/Selectors/OrdersLoading.selector';
import sidebarOpenSelector from '../../State/Store/Sidebar/Selectors/SidebarOpen.selector';
import sidebarActionCreators from '../../State/Store/Sidebar/Sidebar.actions';
import uiActionCreators from '../../State/Store/UI/UI.actions';
import { UI_ORDER_LIST_REFRESH, UI_PROJECT_SELECTOR_PROJECT_REMOVED, UI_PROJECT_SELECTOR_PROJECT_SELECTED } from '../../State/Store/UI/UI.types';
import isIOS from '../../Support/Is/IOS/IsIOS.support';
import themeCreateInjectableStyles from '../../Support/Theme/CreateInjectableStyles/ThemeCreateInjectableStyles.support';
import AddressSearch from '../AddressSearch/AddressSearch.component';
import ChipSelector from '../ChipSelector/ChipSelector.component';
import DateNav from '../DateNav/DateNav.component';
import I18n from '../I18n/I18n.component';
import OrderAccountSwitcher from '../OrderAccountSwitcher/OrderAccountSwitcher.component';
import OrderList from '../OrderList/OrderList.component';
import OrderStatusGroup from '../OrderStatusGroup/OrderStatusGroup.component';
import Sidebar from '../Sidebar/Sidebar.component';
import Spinner from '../Spinner/Spinner.component';
import OrdersDashboardPropTypes from './OrdersDashboard.component.props';
import ordersDashboardStyles from './OrdersDashboard.component.styles';
import { MFSDR } from 'Constants/MouseFlow/SensitiveDataReplacer';
import hitGTM from 'State/Store/UI/Actions/GTM.action';
import { UI_REFRESH_ORDERS_LIST_BUTTON } from 'State/Store/UI/UI.types';

/**
 * The Dashboard component
 * @class OrdersDashboardComponent
 * @extends {React.PureComponent}
 */
class OrdersDashboardComponent extends React.Component {
  topNodeRef = React.createRef();

  state = {
    isLoading: false,
  };

  static propTypes = OrdersDashboardPropTypes;

  /**
   * When the Component mounts, retrieve user accounts and projects
   * @returns {void} void
   * @memberof OrdersDashboardComponent
   */
  async componentDidMount() {
    const { services, filters } = this.props;
    this.setState({ loadingProjects: true });
    const retrieved = await Promise.all([await services.userService.getUserAccounts(), await services.orderService.getProjects(filters.account)]);
    this.setState({
      loadingProjects: false,
      accounts: retrieved[0].accounts,
      projects: Array.isArray(retrieved[1]) ? retrieved[1] : [],
      selectedProjectName: filters.project,
    });
  }

  /**
   * getDerivedStateFromProps
   * @param {any} props props
   * @param {any} state state
   * @returns {Object} state
   * @memberof OrdersDashboardComponent
   */
  static getDerivedStateFromProps(props, state) {
    return {
      isLoading: props.ordersLoading,
    };
  }

  /**
   * componentDidUpdate
   * @param {Object} prevProps props
   * @returns {void} void
   * @memberof OrdersDashboardComponent
   */
  async componentDidUpdate(prevProps) {
    const { services, sidebarOpen, width, filters } = this.props;

    // [iOS only issue] body scroll workaround:
    // The only reason why `enableBodyScroll` is called here is that all attempts
    // to isolate `disableBodyScroll` on the SideBar component failed.
    // Especially for the right sidebar in the main page which will overflow.
    // The only caveat is that while the sidebars are open, body scroll is enabled
    // therefore, the user will experience some weird scrolling issues.
    // But this particular scrolling issue is minor compared to a much bigger
    // issue where the main order list is unscrollable when trying to scroll on
    // the fixed elements then scrolling the main order list.
    // So this is the compromise for now due to time constraints.
    if (isIOS() && isWidthDown(`md`, width)) {
      if (sidebarOpen) {
        enableBodyScroll(this.topNodeRef.current);
      }
    }
    if (prevProps.filters.account !== filters.account) {
      const retrieved = await services.orderService.getProjects(filters.account);
      // below setState is in conditional
      this.setState({ projects: retrieved, selectedProjectName: null }); /* eslint-disable-line react/no-did-update-set-state */
    }
    if (prevProps.filters.project !== filters.project) {
      // below setState is in conditional
      this.setState({ selectedProjectName: filters.project }); /* eslint-disable-line react/no-did-update-set-state */
    }
    return;
  }

  /**
   * onRefresh
   * @returns {void} void
   * @memberof OrdersDashboardComponent
   */
  onRefresh = () => {
    const { actions } = this.props; /* eslint-disable-line react/prop-types */
    this.topNodeRef.current.scrollTop = 0;
    actions.ordersAll();
    actions.uiTriggered({ type: UI_ORDER_LIST_REFRESH });
    this.setState(() => {
      return {
        isLoading: true,
      };
    });
  };

  /**
   * onDateNavEvent
   * @returns {void} void
   */
  onDateNav = () => {
    this.topNodeRef.current.scrollTop = 0;
  };

  /**
   * Selects a project and updates filters
   * @param {string} projectName chips name
   * @returns {void} void
   * @memberof OrdersDashboardComponent
   */
  onProjectSelect = (projectName) => {
    const { actions } = this.props;
    actions.uiTriggered({
      payload: {
        data: {
          name: `projectSelectorProjectSelected`,
        },
      },
      type: UI_PROJECT_SELECTOR_PROJECT_SELECTED,
    });
    actions.filtersSelectProject(projectName);
    this.setState({ selectedProjectName: projectName });
    return;
  };

  /**
   * Deletes selected project
   * @param {string} projectName chips name
   * @returns {void} void
   * @memberof OrdersDashboardComponent
   */
  onProjectDelete = () => {
    const { actions } = this.props;
    actions.uiTriggered({
      payload: {
        data: {
          name: `projectSelectorProjectRemoved`,
        },
      },
      type: UI_PROJECT_SELECTOR_PROJECT_REMOVED,
    });
    actions.filtersSelectProject(null);
    this.setState({ selectedProjectName: null });
    return;
  };

  /**
   * Render the right `<Sidebar />` conditionally
   * @returns {React.Component} The `<Sidebar />`
   * @memberof OrdersDashboardComponent
   */
  renderRightSidebar = () => {
    const { classes, hideSidebarRight } = this.props;
    const { projects, selectedProjectName, loadingProjects } = this.state;

    if (hideSidebarRight) {
      return null;
    }
    // Note: This wrapping `div` is required
    return (
      <div className={cn(classes.sidebar)}>
        <Sidebar anchor="right" permSizing={{ smDown: true }} tempSizing={{ mdUp: true }}>
          <div className={classes.refineHeader}>
            <Typography variant="h6">Filters</Typography>
          </div>
          <Divider variant="middle" />
          <OrderStatusGroup />
          {projects /* || suburbs */ && <Divider variant="middle" />}
          {loadingProjects ? (
            <Spinner size={20} loaderStyles={classes.projectSpinner} />
          ) : (
            projects &&
            !selectedProjectName && (
              <Card classes={{ root: classes.sidebarCard }} elevation={0}>
                <CardHeader
                  classes={{ action: classes.sidebarCardHeaderAction, root: classes.sidebarCardHeader }}
                  title={<Typography variant="body2">Project</Typography>}
                />
                <CardContent>
                  <ChipSelector
                    chips={projects}
                    onSelect={this.onProjectSelect}
                    errorText="Project you entered doesn't exist"
                    placeholder="Search for a project"
                  />
                </CardContent>
              </Card>
            )
          )}
          {selectedProjectName && (
            <div className={classes.chipSelectorContainer}>
              {` `}
              <Card classes={{ root: classes.sidebarCard }} elevation={0}>
                <CardHeader
                  classes={{ action: classes.sidebarCardHeaderAction, root: classes.sidebarCardHeader }}
                  title={<Typography variant="body2">Project</Typography>}
                />
                <CardContent data-mf-replace-inner={MFSDR}>
                  <Tooltip title={selectedProjectName}>
                    <Chip
                      icon={<FolderIcon className={classes.folderIcon} />}
                      label={selectedProjectName}
                      variant="outlined"
                      classes={{ root: classes.chipRoot, label: classes.chipLabel }}
                      onDelete={this.onProjectDelete}
                    />
                  </Tooltip>
                  {` `}
                </CardContent>
              </Card>
            </div>
          )}
          <Divider variant="middle" />
          <AddressSearch />
        </Sidebar>
      </div>
    );
  };

  /**
   * Open the refine menu
   * @returns {void} void
   * @memberof OrdersDashboardComponent
   */
  handleRefineToggle = () => {
    const { actions } = this.props;
    actions.sidebarRightToggle();
    return;
  };

  /**
   * Refreshes and reports to GTM
   * @returns {void} void
   * @memberof OrdersDashboardComponent
   */
  handleRefreshButton = () => {
    const { actions } = this.props;
    actions.uiTriggered({
      payload: {
        data: {
          name: `ordersListRefreshButton`,
        },
      },
      type: UI_REFRESH_ORDERS_LIST_BUTTON,
    });
    this.onRefresh();
    return;
  };

  renderPageHeader = () => {
    const { classes, headingData, heading, width } = this.props;
    const { accounts } = this.state;
    const isSmall = isWidthDown(`sm`, width);
    const refreshButton = (
      <IconButton className={classes.refreshButton} color="inherit" onClick={this.handleRefreshButton}>
        <RefreshIcon />
      </IconButton>
    );
    return (
      <React.Fragment>
        {!isSmall ? (
          <Grid alignItems="center" className={classes.pageHeaderContainer} container justify="space-between">
            <Grid item>
              <I18n color="inherit" data={headingData} variant="h4">
                {heading}
              </I18n>
            </Grid>
            <Grid item sm className={classes.accountSwitcher}>
              <OrderAccountSwitcher accounts={accounts} />
            </Grid>
            <Grid item sm={6} style={{ textAlign: `right` }}>
              {refreshButton}
              <DateNav onDateNav={this.onDateNav} />
            </Grid>
          </Grid>
        ) : (
          <Grid alignItems="center" className={cn(classes.pageHeaderContainerSmall, classes.flexWrapNowrap)} container justify="flex-end">
            <Grid item>
              {refreshButton}
              <OrderAccountSwitcher isMobile accounts={accounts} />
              <DateNav onDateNav={this.onDateNav} />
              <IconButton color="inherit" onClick={this.handleRefineToggle}>
                <TuneIcon />
              </IconButton>
            </Grid>
          </Grid>
        )}
        <Divider />
      </React.Fragment>
    );
  };

  /**
   * Returns the rendered OrdersDashboardComponent
   * @returns {React.Component} the rendered OrdersDashboardComponent
   * @memberof OrdersDashboardComponent
   */
  render() {
    const { largeNavBar, overflow, alwaysCenter, padding, classes } = this.props; /* eslint-disable-line react/prop-types */
    const { isLoading, accounts } = this.state;
    return (
      <React.Fragment>
        {this.renderPageHeader()}
        <div className={cn(classes.stage)}>
          <div className={cn(alwaysCenter ? classes.alwaysCenter : classes.content, overflow === `auto` && classes.overflowAuto)}>
            <main
              className={cn(classes.main, {
                [classes.paddingDense]: padding === `dense`,
                [classes.paddingNone]: padding === `none`,
                [classes.paddingNormal]: padding === `normal`,
              })}
            >
              <div className={classes.root}>
                <Fade in={isLoading}>
                  <LinearProgress className={classes.loader} />
                </Fade>
                <div className={classes.orderListContainer} ref={this.topNodeRef}>
                  <AccountsContext.Provider value={accounts}>
                      <OrderList onRefresh={this.onRefresh} />
                  </AccountsContext.Provider>
                </div>
              </div>
            </main>
          </div>
          {!largeNavBar && this.renderRightSidebar()}
        </div>
      </React.Fragment>
    );
  }
}

const OrdersDashboard = compose(
  withWidth(),
  withServices,
  withStyles(themeCreateInjectableStyles(ordersDashboardStyles), { withTheme: true }),
  connect(
    (state) => {
      // [iOS only issue] body scroll workaround:
      // The only reason why this component needs to know the sidebar open state
      // is to enable the check to be done in the componentDidUpdate.
      const sideBarOpenLeft = sidebarOpenSelector(`left`);
      const sideBarOpenRight = sidebarOpenSelector(`right`);
      return {
        filters: getFilters(state),
        ordersLoading: ordersLoadingSelector(state),
        sidebarOpen: sideBarOpenLeft(state) || sideBarOpenRight(state),
      };
    },
    (dispatch) => ({
      actions: bindActionCreators(
        {
          ...filtersActionCreators,
          ...sidebarActionCreators,
          ...ordersActionCreators,
          ...uiActionCreators,
        },
        dispatch,
      ),
    }),
  ),
)(OrdersDashboardComponent);

export default OrdersDashboard;
